import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import Highcharts from '../../../highchartsInit'
import { Chart as HighchartsReact } from '@highcharts/react'
import { round } from 'altrac-conversions'
import moment from 'moment'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'

import { isNumber } from '../Inputs/Inputs'
import ChartDataLoading from './ChartDataLoading'
import AppDispatcher from '../../../dispatchers/AppDispatcher'
import AppStore from '../../../stores/AppStore'

import styles from '../../../../css/telemetry.module.css'

function Graph (props) {
  const {
    deviceReading,
    disableCalibration,
    calibrate1,
    calibrate2,
    rpmMultiplier,
    formatMessage,
    chartType,
    device,
    tempPreference,
  } = props

  const rpmData = []
  const temperatureData = []
  const startTempData = []
  const stopTempData = []
  const callToRunData = []

  const autoSwitchData = []
  const modeData = []
  const serialCommsData = []

  const batteryData = []

  deviceReading
    .sort((a, b) => {
      const dateA = new Date(a.date)
      const dateB = new Date(b.date)
      return dateA - dateB
    })
    .forEach((item) => {
      if (item) {
        if (chartType === 'rpm') {
          if (isNumber(item['134'])) {
            rpmData.push([
              moment(item.date).valueOf(),
              round(Number(item['134'])),
            ])
          }
          if (isNumber(item['128'])) {
            temperatureData.push([
              moment(item.date).valueOf(),
              round(Number(item['128'])),
            ])
          }
          if (isNumber(item['200'])) {
            startTempData.push([
              moment(item.date).valueOf(),
              round(Number(item['200'])),
            ])
          }
          if (isNumber(item['201'])) {
            stopTempData.push([
              moment(item.date).valueOf(),
              round(Number(item['201'])),
            ])
          }
          if (isNumber(item['131'])) {
            callToRunData.push([
              moment(item.date).valueOf(),
              round(Number(item['131'])),
            ])
          }
        } else if (chartType === 'mode') {
          const start = moment(item.date).toISOString()
          const stop = moment(item.date).add(15, 'minutes').toISOString()
          if (item['143'] === 1) {
            autoSwitchData.push({
              x: moment(start).valueOf(),
              x2: moment(stop).valueOf(),
              y: 0,
              color: '#108157',
            })
          }

          if (item['202'] === 1) {
            modeData.push({
              x: moment(start).valueOf(),
              x2: moment(stop).valueOf(),
              y: 1,
              color: '#2870D6',
            })
          } else if (item['202'] === 0) {
            modeData.push({
              x: moment(start).valueOf(),
              x2: moment(stop).valueOf(),
              y: 1,
              color: '#B4B3BB',
            })
          }

          if (item['140']) {
            if (item['140'] === 1) {
              serialCommsData.push({
                x: moment(start).valueOf(),
                x2: moment(stop).valueOf(),
                y: 2,
                color: '#108157',
              })
            } else if (item['140'] === 0) {
              serialCommsData.push({
                x: moment(start).valueOf(),
                x2: moment(stop).valueOf(),
                y: 2,
                color: '#B4B3BB',
              })
            }
          }
        } else if (chartType === 'battery') {
          if (isNumber(item['130'])) {
            const batteryVoltage = item['130'] * 21.633
            const fixedBatteryVoltage = batteryVoltage.toFixed(1)
            batteryData.push([
              moment(item.date).valueOf(),
              Number(fixedBatteryVoltage),
            ])
          }
        }
      }
    })

  let series
  let plotLines
  let plotOptionsValue = {}
  let yAxisValue
  let tooltipValue = {}
  let legendValue = {}

  if (chartType === 'rpm') {
    yAxisValue = [
      {
        labels: {
          format: '{value}',
          style: {
            color: '#6AAEF8',
          },
        },
        title: {
          text: `°${tempPreference}`,
          style: {
            color: '#6AAEF8',
            textAnchor: 'start',
          },
        },
        opposite: true,
        min: -10,
        maxRange: 90,
      },
      {
        gridLineWidth: 0,
        labels: {
          format: '{value}',
        },
        title: {
          text: 'RPM',
          style: {
            color: '#284047',
          },
        },
        min: 0,
        minRange: 1,
      },
      {
        gridLineWidth: 0,
        visible: false,
        min: 0,
        minRange: 1,
      },
    ]
    tooltipValue = {
      xDateFormat: '%b %e %H:%M',
      shared: true,
    }
    series = [
      {
        turboThreshold: 6000,
        name: 'RPM',
        data: rpmData,
        type: 'spline',
        yAxis: 1,
        color: '#108157',
        zIndex: 1,
      },
      {
        turboThreshold: 6000,
        name: `Temperature °${tempPreference}`,
        data: temperatureData,
        type: 'spline',
        yAxis: 0,
        color: '#0B1424',
        zIndex: 1,
      },
      {
        turboThreshold: 6000,
        name: `Start Temperature °${tempPreference}`,
        data: startTempData,
        type: 'spline',
        yAxis: 0,
        color: '#2870D6',
        zIndex: 1,
      },
      {
        turboThreshold: 6000,
        name: `Stop Temperature °${tempPreference}`,
        data: stopTempData,
        type: 'spline',
        yAxis: 0,
        color: '#BA1615',
        zIndex: 1,
      },
      {
        turboThreshold: 6000,
        name: 'Call To Run',
        data: callToRunData,
        type: 'column',
        yAxis: 2,
        color: 'rgba(21, 174, 36, .5)',
        pointWidth: 20,
        zIndex: 0,
      },
    ]
  } else if (chartType === 'mode') {
    yAxisValue = {
      title: {
        text: '',
      },
      categories: ['Auto Switch', 'Mode', 'Serial Comms'],
      reversed: true,
    }

    legendValue = {
      enabled: false,
    }

    plotOptionsValue = {
      xrange: {
        dataLabels: {
          enabled: true,
        },
        minPointLength: 1,
        enableMouseTracking: true,
        animation: false,
      },
      scatter: {
        dataLabels: {
          enabled: true,
        },
        color: 'none',
        marker: {
          enabled: true,
          fillColor: '#23ce6b',
          lineColor: 'white',
          lineWidth: 1,
        },
        states: {
          hover: { enabled: false },
          inactive: { enabled: false },
        },
      },
    }

    tooltipValue = {
      backgroundColor: 'white',
      borderColor: '#284047',
      borderRadius: 3,
      borderWidth: 1,
      shadow: false,
      distance: 12,
      outside: true,
      useHTML: true,
      shape: 'square',
    }
    series = [
      {
        turboThreshold: 6000,
        name: 'Auto Switch',
        data: autoSwitchData,
        type: 'xrange',
        pointRange: 1,
        borderColor: 'none',
        borderRadius: 0,
        pointWidth: 20,
      },
      {
        turboThreshold: 6000,
        name: 'Mode',
        data: modeData,
        type: 'xrange',
        pointRange: 1,
        borderColor: 'none',
        borderRadius: 0,
        pointWidth: 20,
      },
      {
        turboThreshold: 6000,
        name: 'Serial Comms',
        data: serialCommsData,
        type: 'xrange',
        pointRange: 1,
        borderColor: 'none',
        borderRadius: 0,
        pointWidth: 20,
      },
    ]
  } else if (chartType === 'battery') {
    yAxisValue = [
      {
        labels: {
          format: '{value}',
          style: {
            color: '#666666',
          },
        },
        title: {
          text: 'Volts',
          style: {
            color: '#666666',
            textAnchor: 'start',
          },
        },
        opposite: false,
        min: 0,
        maxRange: 25,
      },
    ]
    tooltipValue = {
      xDateFormat: '%b %e %H:%M',
      shared: true,
    }
    series = [
      {
        turboThreshold: 6000,
        name: 'Battery',
        data: batteryData,
        type: 'spline',
        yAxis: 0,
        color: '#0B1424',
        zIndex: 1,
      },
    ]
  }
  plotLines = []

  return (
    <div>
      <HighchartsReact
        highcharts={Highcharts}
          options={{
            accessibility: {
              enabled: false,
            },
            title: {
              text: '',
            },
            tooltip: tooltipValue,
          legend: legendValue,
          xAxis: {
            type: 'datetime',
            dateTimeLabelFormats: {
              day: '%e %b',
              month: '%b %e',
              year: '%b',
            },
            title: {
              enabled: false,
            },
          },
          yAxis: yAxisValue,
          series,
          colors: ['#23ce6b'],
          plotOptions: plotOptionsValue,
          exporting: {
            enabled: false,
          },
          credits: {
            enabled: false,
          },
        }}
      />
      {chartType === 'mode' && (
        <div className={styles.modeChartLegend}>
          <span className={`${styles.legendItem} ${styles.legendOn}`}>
            <span />
            On
          </span>
          <span className={`${styles.legendItem} ${styles.legendAuto}`}>
            <span />
            Auto
          </span>
          <span className={`${styles.legendItem} ${styles.legendManual}`}>
            <span />
            Manual
          </span>
        </div>
      )}
    </div>
  )
}

Graph.propTypes = {
  deviceReading: PropTypes.array.isRequired,
  chartType: PropTypes.string.isRequired,
  device: PropTypes.object.isRequired,
  tempPreference: PropTypes.string.isRequired,
  disableCalibration: PropTypes.bool,
  calibrate1: PropTypes.number,
  calibrate2: PropTypes.number,
  rpmMultiplier: PropTypes.number,
  formatMessage: PropTypes.func,
}

const TelemetryWindMachine = function TelemetryWindMachine (props) {
  const {
    device,
    sensorData,
    metricReady,
    location: {
      query: { dataType } = {},
    },
    periodStart,
    periodStop,
  } = props

  const user = AppStore.getUser()
  const {
    locale: { tempConv: tempPreference },
  } = user
  const intervalRef = useRef(null)

  const getNewData = (address, periodStartParam, periodStopParam) => {
    AppDispatcher.dispatch({
      action: 'get-metric-address',
      address: address || device.address,
      timeBeg: periodStartParam || periodStart,
      timeEnd: periodStopParam || periodStop,
    })
  }

  useEffect(() => {
    getNewData()
    intervalRef.current = setInterval(getNewData, 60000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  useEffect(() => {
    getNewData(null, periodStart, periodStop)
  }, [periodStart, periodStop])

  if (!Array.isArray(sensorData) || sensorData.length < 1 || !metricReady) {
    return <ChartDataLoading />
  }

  const keys = []
  sensorData.every((i, x) => {
    keys.push(...Object.keys(i))
    return true
  })
  const keysU = [...new Set(keys)]
  keysU.sort()
  keysU.sort((a, b) => {
    if (a !== 'date' && b === 'date') {
      return 1
    }
    if (a === 'date' && b !== 'date') {
      return -1
    }
    return 0
  })
  const columnDefs = keysU.reduce((acc, d) => {
    if (d === 'webhook') {
      return acc
    }
    let filter
    let type = 'numericColumn'
    let width
    let pinned
    let valueFormatter
    if (d === 'date') {
      filter = 'agDateColumnFilter'
      type = 'dateColumn'
      width = 220
      pinned = 'left'
    }
    if (d === 'R1' || d === 'R2') {
      pinned = 'left'
    }

    acc.push({
      field: d,
      sortable: true,
      filter,
      type,
      width,
      pinned,
      valueFormatter,
    })
    return acc
  }, [])

  const dateFormatterCurrentMin = (params) => {
    const date = moment(params.value).format('LLL')
    return date
  }

  const voltageFormatter = (params) => {
    const voltage = params.value * 21.633
    return voltage.toFixed(1)
  }

  return (
    <div>
      <div className={`${styles.pageWidth} ${styles.windMachineDatePicker}`}>
        <Graph
          deviceReading={sensorData}
          chartType='rpm'
          device={device}
          tempPreference={tempPreference}
        />
      </div>
      <div className={`${styles.pageWidth} ${styles.windMachineDatePicker}`}>
        <Graph
          deviceReading={sensorData}
          chartType='mode'
          device={device}
          tempPreference={tempPreference}
        />
      </div>
      <div className={`${styles.pageWidth} ${styles.windMachineDatePicker}`}>
        <Graph
          deviceReading={sensorData}
          chartType='battery'
          device={device}
          tempPreference={tempPreference}
        />
      </div>
      <div className={`${styles.pageWidth} ${styles.windMachineDatePicker}`}>
        <div className='ag-theme-alpine' style={{ height: 500, width: '100%' }}>
          <AgGridReact
            rowData={sensorData}
            columnDefs={[
              {
                headerName: 'Date',
                field: 'date',
                sortable: true,
                type: 'numericColumn',
                valueFormatter: dateFormatterCurrentMin,
              },
              {
                headerName: 'RPM',
                field: '134',
                sortable: true,
                type: 'numericColumn',
              },
              {
                headerName: `Temperature °${tempPreference}`,
                field: '128',
                sortable: true,
                type: 'numericColumn',
              },
              {
                headerName: `Start Temp °${tempPreference}`,
                field: '200',
                sortable: true,
                type: 'numericColumn',
              },
              {
                headerName: `Stop Temp °${tempPreference}`,
                field: '201',
                sortable: true,
                type: 'numericColumn',
              },
              {
                headerName: 'Call To Run',
                field: '131',
                sortable: true,
                type: 'numericColumn',
              },
              {
                headerName: 'Battery',
                field: '130',
                sortable: true,
                type: 'numericColumn',
                valueFormatter: voltageFormatter,
              },
              {
                headerName: 'Auto Switch',
                field: '143',
                sortable: true,
                type: 'numericColumn',
              },
              {
                headerName: 'Mode',
                field: '202',
                sortable: true,
                type: 'numericColumn',
              },
              {
                headerName: 'Serial Comms',
                field: '140',
                sortable: true,
                type: 'numericColumn',
              },
            ]}
            defaultColDef={{
              width: 100,
              editable: false,
              filter: 'agNumberColumnFilter',
              resizable: true,
            }}
          />
        </div>
      </div>
    </div>
  )
}

TelemetryWindMachine.propTypes = {
  device: PropTypes.shape({
    address: PropTypes.string,
  }).isRequired,
  sensorData: PropTypes.array.isRequired,
  metricReady: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    query: PropTypes.shape({
      dataType: PropTypes.string,
    }),
  }).isRequired,
  periodStart: PropTypes.string,
  periodStop: PropTypes.string,
}

export default TelemetryWindMachine
